import React, { CSSProperties, PropsWithChildren, RefObject } from 'react';
import ReactDOM from 'react-dom';
import classnames from 'classnames';
import { gikClassPrefix } from '@gik/core/constants';
import Image from '@gik/ui/Image/Image';
import { UISelectVariant, UISize } from '@gik/ui/types';
import ReactSelect, { Props, StylesConfig, OptionProps, components } from 'react-select';
import { useScroll, useWindowScroll } from 'react-use';
import SvgIcon from '../SvgIcon';
import FontIcon from '../FontIcon';
import bemBlock from '@gik/core/utils/bemBlock';

const bem = bemBlock('select');

const { Option, SingleValue } = components;
const OptionWithIcon = (props: PropsWithChildren<OptionProps<any, boolean>>) => {
  const className = bem(
    'option',
    [{ icon: props.data.icon }, { fonticon: props.data.fontIcon }, { image: props.data.image }],
    props.className
  );

  return (
    <Option {...props} className={className}>
      {props.data.icon && <SvgIcon name={props.data.icon} />}
      {props.data.fontIcon && <FontIcon name={props.data.fontIcon} />}
      {props.data.image && <Image src={props.data.image} />}
      <span>{props.data.label}</span>
    </Option>
  );
};

const SingleValueWithIcon = (props: PropsWithChildren<OptionProps<any, boolean>>) => {
  const className = bem(
    'single-value',
    [{ icon: props.data.icon }, { fonticon: props.data.fontIcon }, { image: props.data.image }],
    props.className
  );

  return (
    <SingleValue {...props} className={className}>
      {props.data.icon && <SvgIcon name={props.data.icon} />}
      {props.data.fontIcon && <FontIcon name={props.data.fontIcon} />}
      {props.data.image && <Image src={props.data.image} />}
      <span>{props.data.label}</span>
    </SingleValue>
  );
};

export interface SelectOptionsType<T = string> {
  label: React.ReactNode;
  value: T;
}

// TODO: remove - should not use fontIcon
export interface SelectOptionsTypeWithFontIcon<T = string> extends SelectOptionsType<T> {
  fontIcon?: string;
}

export interface SelectOptionsTypeWithIcon<T = string> extends SelectOptionsType<T> {
  icon?: string;
}

export interface SelectOptionsTypeWithImage<T = string> extends SelectOptionsType<T> {
  image?: string;
}

//export interface ISelectProps<OptionType extends OptionTypeBase = SelectOptionsType> extends Props<OptionType> {
export interface ISelectProps<T = string>
  extends Omit<React.HTMLAttributes<HTMLElement>, 'tabIndex' | 'value' | 'onChange' | 'autoComplete' | 'defaultValue'>,
    Pick<Props, 'tabIndex' | 'menuPlacement' | 'instanceId' | 'isLoading'> {
  variant?: UISelectVariant;
  size?: UISize;
  append?: React.ReactNode;
  prepend?: React.ReactNode;
  block?: boolean;
  hover?: boolean;
  active?: boolean;
  focus?: boolean;
  disabled?: boolean;
  loading?: boolean;
  rounded?: boolean;
  squared?: boolean;
  dropdownAutoWidth?: boolean;
  style?: object;
  styles?: StylesConfig<any, boolean>;
  scrollParent?: RefObject<HTMLElement>;
  hasError?: boolean;
  hasWarning?: boolean;
  hasSuccess?: boolean;
  options?: SelectOptionsType<T>[];
  value?: T;
  isSearchable?: boolean;
  menuPortalTarget?: HTMLElement | null;
  autoComplete?: string;
  creatable?: boolean;
  onChange?: (value: T) => void;
  // FIXME: components
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  components?: any;
  DropdownIndicator?: React.FC;
  darkPlaceholder?: boolean; // use a darker text color for the placeholder (used by AddToCalendarSelector because it appears in a notification which should have more contrast)
}

const blockName = `${gikClassPrefix}-select`;

function Select<T = string>({
  variant = 'default',
  size = 'base',
  tabIndex,
  block,
  hover,
  active,
  focus,
  disabled,
  loading,
  rounded,
  squared,
  creatable,
  onClick,
  onFocus,
  onBlur,
  className,
  value,
  options,
  menuPortalTarget,
  style,
  styles,
  dropdownAutoWidth,
  isSearchable = false,
  // FIXME: Bruno
  // eslint-disable-next-line react-hooks/rules-of-hooks
  scrollParent = React.useRef(null),
  hasError,
  hasWarning,
  hasSuccess,
  autoComplete,
  onChange,
  DropdownIndicator,
  darkPlaceholder,
  ...otherProps
}: ISelectProps<T>) {
  // ref: React.Ref<ReactSelect>
  const [focused, setFocused] = React.useState(false);
  const [internalValue, setInternalValue] = React.useState<SelectOptionsType<T>>(null);
  const [isAutofilled, setIsAutofilled] = React.useState<boolean>(false);

  const root = React.useRef<HTMLDivElement>(null);
  const calculatedScrollParent = React.useRef<HTMLElement>(null);
  const portalRef = React.useRef<HTMLDivElement>(null);
  const autofillRef = React.useRef<HTMLSelectElement>(null);

  useScroll(scrollParent.current ? scrollParent : calculatedScrollParent);
  useWindowScroll();

  React.useEffect(() => {
    if (root.current && !scrollParent.current) {
      calculatedScrollParent.current = root.current.parentElement;
    }
  }, [root.current, scrollParent.current]);

  React.useEffect(() => {
    if (focus !== null && focus !== undefined) {
      setFocused(focus);
    }
  }, [focus]);

  React.useEffect(() => {
    if (options) {
      const _value = findInternalMatchingValue(value);
      if (_value && _value.value !== internalValue?.value) {
        setInternalValue(_value);
      }
      if (!_value) setInternalValue(null);
    }
  }, [value, options]);

  React.useEffect(() => {
    if (options && autofillRef.current?.value) {
      const value = (autofillRef.current?.value as unknown) as T;
      const foundValue = findInternalMatchingValue(value);

      if (foundValue) {
        if (onChange) {
          onChange(foundValue.value);
        }

        setInternalValue(foundValue);
        setIsAutofilled(true);
        autofillRef.current.value = null;
      }
    }
  }, [autofillRef.current?.value, options]);

  function findInternalMatchingValue(value: T) {
    return options.find(optionValue => optionValue.value === value);
  }

  function handleChange(option: SelectOptionsType<T>) {
    if (option) {
      if (onChange) {
        onChange(option.value);
      }
      setIsAutofilled(false);
    }
  }

  function handleClick(ev: React.MouseEvent<HTMLElement>): void {
    if (disabled) {
      return;
    }

    if (onClick) {
      onClick(ev);
    }
  }

  function handleFocus(ev: React.FocusEvent<HTMLElement>): void {
    if (disabled) {
      return;
    }

    setFocused(true);

    if (onFocus) {
      onFocus(ev);
    }
  }

  function handleBlur(ev: React.FocusEvent<HTMLElement>): void {
    if (focus !== true) {
      setFocused(false);
    }

    if (onBlur) {
      onBlur(ev);
    }
  }

  const finalTabIndex = disabled ? '-1' : tabIndex;

  const classNames: string = classnames([
    { [blockName]: true },
    { [`${blockName}--${variant}`]: variant },
    { [`${blockName}--size-${size}`]: size },
    { [`${blockName}--active`]: active },
    { [`${blockName}--hover`]: hover },
    { [`${blockName}--block`]: block },
    { [`${blockName}--disabled`]: disabled },
    { [`${blockName}--focus`]: focused },
    { [`${blockName}--loading `]: loading },
    { [`${blockName}--squared `]: squared },
    { [`${blockName}--rounded `]: rounded },
    { [`${blockName}--has-error`]: hasError },
    { [`${blockName}--has-warning`]: hasWarning },
    { [`${blockName}--has-success`]: hasSuccess },
    { [`${blockName}--autofilled`]: isAutofilled },
    { [`${blockName}--darkPlaceholder`]: darkPlaceholder },
    { ['is-text']: variant.endsWith('-text') },
    className || '',
  ]);

  const customStyles = {
    ...styles,
    menu: (provided: object) => {
      const menuStyle: CSSProperties = {
        ...provided,
      };
      if (dropdownAutoWidth) menuStyle.width = 'auto';
      return menuStyle;
    },
  };

  const components = { Option: OptionWithIcon, SingleValue: SingleValueWithIcon, DropdownIndicator };

  // remove DropdownIndicator key if it is empty. This is to make sure the default dropdown indicator will be used as a fallback.
  if (!DropdownIndicator) delete components.DropdownIndicator;

  const selectProps = {
    tabIndex: finalTabIndex,
    onClick: handleClick,
    onFocus: handleFocus,
    onBlur: handleBlur,
    onChange: handleChange,
    isDisabled: disabled,
    isLoading: loading,
    creatable: creatable,
    value: internalValue,
    options,
    style,
    styles: customStyles,
    isSearchable,
    components,
    [menuPortalTarget !== null && 'menuPortalTarget']: portalRef.current,
    ...otherProps,
  };

  const portalContainer = <div ref={portalRef} className={`${blockName}--portal ${classNames}`} />;

  // FIXME: typings for ref
  return (
    <div className={classNames} ref={root}>
      {menuPortalTarget !== null && ReactDOM.createPortal(portalContainer, menuPortalTarget || document.body)}

      <ReactSelect
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        // ref={ref as any}
        classNamePrefix={blockName}
        {...selectProps}
        menuPosition="fixed"
        menuPlacement="auto"
        menuShouldScrollIntoView
      />

      {autoComplete && (
        <select className={`${blockName}__autofill`} aria-hidden ref={autofillRef} autoComplete={autoComplete}>
          <option value={null} />
          {options.map((option, i) => (
            <option key={i} value={option.value.toString()}>
              {option.label} test
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

// export default React.forwardRef<ReactSelect, ISelectProps>(Select);
export default Select;
